import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  TEAM_ROLE_COMPARE_COLUMNS,
  TEAM_ROLE_COMPARE_INTRO,
  TEAM_ROLE_CONSULTA_NOTE,
  TEAM_ROLE_LOCKED_IDS,
  mergeTeamRolePermissionRows,
  permissionCellLabel,
} from "@/lib/teamRolePermissions";
import {
  AUXILIARY_DESPACHO_ROLES,
  AUXILIARY_ROLES_GUIDE,
  despachoRoleLabel,
} from "@/lib/auxiliaryRoleGuide";
import { isWorkspaceUserActive } from "@/lib/workspaceTeamLimits";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

function MatrixCell({
  allowed,
  locked,
  editable,
  busy,
  onToggle,
  "aria-label": ariaLabel,
  testId,
}) {
  if (locked || !editable) {
    const yes = allowed === true;
    const no = allowed === false;
    return (
      <span
        className={cn(
          "inline-flex min-w-[2rem] justify-center rounded px-2 py-0.5 text-[10px] font-semibold",
          yes
            ? "bg-emerald-100 text-emerald-800"
            : no
              ? "bg-slate-100 text-slate-600"
              : "bg-amber-50 text-amber-900",
        )}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {permissionCellLabel(allowed)}
      </span>
    );
  }

  return (
    <div className="inline-flex items-center justify-center gap-1.5" data-testid={testId}>
      <Switch
        checked={Boolean(allowed)}
        disabled={busy}
        onCheckedChange={(next) => onToggle(Boolean(next))}
        aria-label={ariaLabel}
        className="data-[state=checked]:bg-emerald-600"
      />
      <span
        className={cn(
          "text-[10px] font-semibold tabular-nums",
          allowed ? "text-emerald-800" : "text-slate-500",
        )}
      >
        {allowed ? "Sí" : "No"}
      </span>
    </div>
  );
}

/**
 * Matriz Auxiliar vs Administrador (editable) + controles por auxiliar
 * (perfil y activar/desactivar acceso).
 */
export default function TeamRolePermissionsPanel({
  users = [],
  loading = false,
  busy = false,
  canEditMatrix = false,
  onToggleAccess,
  onChangeRole,
}) {
  const manageables = (users || []).filter((u) => !u.is_admin_owner && !u.is_buyer_tenant);

  const [rows, setRows] = useState(() => mergeTeamRolePermissionRows(null));
  const [matrixLoading, setMatrixLoading] = useState(Boolean(canEditMatrix));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [apiCanEdit, setApiCanEdit] = useState(Boolean(canEditMatrix));

  const loadMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const { data } = await api.get("/admin/team-role-permissions");
      setRows(mergeTeamRolePermissionRows(data?.rows));
      setApiCanEdit(Boolean(data?.can_edit ?? canEditMatrix));
      setDirty(false);
    } catch (err) {
      setRows(mergeTeamRolePermissionRows(null));
      setApiCanEdit(Boolean(canEditMatrix));
      toast.error(
        err?.response?.data?.detail || "No se pudo cargar la matriz de permisos.",
      );
    } finally {
      setMatrixLoading(false);
    }
  }, [canEditMatrix]);

  useEffect(() => {
    void loadMatrix();
  }, [loadMatrix]);

  const editable = Boolean(apiCanEdit);

  const setCell = useCallback((rowId, field, value) => {
    if (TEAM_ROLE_LOCKED_IDS.has(String(rowId || ""))) return;
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, [field]: Boolean(value), editable: true } : r,
      ),
    );
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editable || saving) return;
    setSaving(true);
    try {
      const payload = {
        rows: rows.map((r) => ({
          id: r.id,
          auxiliar: Boolean(r.auxiliar),
          supervisor: Boolean(r.supervisor),
        })),
      };
      const { data } = await api.put("/admin/team-role-permissions", payload);
      setRows(mergeTeamRolePermissionRows(data?.rows));
      setDirty(false);
      toast.success("Permisos del equipo guardados.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudieron guardar los permisos.",
      );
    } finally {
      setSaving(false);
    }
  }, [editable, rows, saving]);

  const handleReset = useCallback(async () => {
    if (!editable || saving) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/team-role-permissions", { rows: [] });
      setRows(mergeTeamRolePermissionRows(data?.rows));
      setDirty(false);
      toast.success("Matriz restaurada a los valores por defecto.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo restaurar la matriz.",
      );
    } finally {
      setSaving(false);
    }
  }, [editable, saving]);

  const matrixBusy = matrixLoading || saving;

  return (
    <div className="space-y-4" data-testid="equipo-permisos-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-3xl">
          <h3
            className="m-0 text-sm font-semibold text-[#0F2D5C]"
            data-testid="equipo-permisos-title"
          >
            Permisos: Auxiliar vs Administrador
          </h3>
          <p className="mb-0 mt-1.5 text-xs leading-relaxed text-slate-600">
            {TEAM_ROLE_COMPARE_INTRO}
          </p>
          {!editable ? (
            <p className="mb-0 mt-1 text-[11px] leading-relaxed text-slate-500">
              Solo el titular (o administrador comprador) puede editar esta matriz.
              Puede seguir cambiando el perfil o el acceso de cada auxiliar abajo.
            </p>
          ) : null}
        </div>
        {editable ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matrixBusy}
              onClick={() => void handleReset()}
              className="h-8 text-xs"
              data-testid="equipo-permisos-reset"
            >
              Restaurar defaults
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={matrixBusy || !dirty}
              onClick={() => void handleSave()}
              className="h-8 gap-1.5 bg-[#0F2D5C] text-xs hover:bg-[#0a2247]"
              data-testid="equipo-permisos-save"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden />
              )}
              Guardar
            </Button>
          </div>
        ) : null}
      </div>

      <div className="overflow-hidden overflow-x-auto rounded-lg border border-slate-200 bg-white">
        {matrixLoading ? (
          <div className="py-8 text-center text-sm text-slate-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-hidden />
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="equipo-permisos-matrix">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="min-w-[200px] px-3 py-2.5 font-semibold text-slate-600">
                  Capacidad
                </th>
                {TEAM_ROLE_COMPARE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="w-[140px] px-3 py-2.5 text-center font-semibold text-[#0F2D5C]"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const locked =
                  Boolean(row.editable === false) ||
                  TEAM_ROLE_LOCKED_IDS.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className="border-t border-slate-100"
                    data-testid={`equipo-permiso-row-${row.id}`}
                  >
                    <td className="px-3 py-2.5 text-slate-700">
                      <span className="font-medium">{row.label}</span>
                      {row.note ? (
                        <span className="mt-0.5 block text-[10px] text-slate-500">
                          {row.note}
                        </span>
                      ) : null}
                      {locked ? (
                        <span className="mt-0.5 block text-[10px] font-medium text-amber-700">
                          No editable (reservado)
                        </span>
                      ) : null}
                    </td>
                    {TEAM_ROLE_COMPARE_COLUMNS.map((col) => (
                      <td key={col.key} className="px-3 py-2.5 text-center align-middle">
                        <MatrixCell
                          allowed={row[col.key]}
                          locked={locked}
                          editable={editable}
                          busy={matrixBusy}
                          onToggle={(v) => setCell(row.id, col.key, v)}
                          aria-label={`${row.label}: ${col.label}`}
                          testId={`equipo-permiso-cell-${row.id}-${col.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p
        className="m-0 text-[11px] leading-relaxed text-slate-600"
        data-testid="equipo-permisos-consulta-note"
      >
        {TEAM_ROLE_CONSULTA_NOTE}
      </p>

      <div className="space-y-2" data-testid="equipo-permisos-operativos">
        <div>
          <h4 className="m-0 text-xs font-semibold text-[#0F2D5C]">
            Activar / desactivar permisos por auxiliar
          </h4>
          <p className="mb-0 mt-1 text-[11px] leading-relaxed text-slate-600">
            Cambie el perfil para conceder o quitar capacidades (Auxiliar, Administrador de
            equipo o Consulta). Use Desactivar para cortar el acceso al espacio sin borrar el
            historial.
          </p>
        </div>

        <div className="overflow-hidden overflow-x-auto rounded-lg border border-slate-200 bg-white">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : manageables.length === 0 ? (
            <p
              className="m-0 px-3 py-6 text-center text-xs text-slate-500"
              data-testid="equipo-permisos-empty"
            >
              No hay auxiliares en esta lista. Agregue uno en la pestaña Usuarios (vista Activos
              o Inactivos).
            </p>
          ) : (
            <table className="w-full text-xs" data-testid="equipo-permisos-roster">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Nombre</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Correo</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Perfil (permisos)</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Acceso</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {manageables.map((u) => {
                  const active = isWorkspaceUserActive(u);
                  const roleValue = u.despacho_role || "auxiliar";
                  return (
                    <tr
                      key={u.id}
                      className="border-t border-slate-100"
                      data-testid={`equipo-permiso-user-${u.id}`}
                    >
                      <td className="px-3 py-2">{u.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{u.email}</td>
                      <td className="px-3 py-2">
                        <Select
                          value={roleValue}
                          onValueChange={(value) => onChangeRole?.(u, value)}
                          disabled={busy || !onChangeRole}
                        >
                          <SelectTrigger
                            className="h-8 min-w-[180px] bg-white text-xs"
                            data-testid={`equipo-permiso-role-${u.id}`}
                          >
                            <SelectValue placeholder={despachoRoleLabel(roleValue)} />
                          </SelectTrigger>
                          <SelectContent>
                            {AUXILIARY_DESPACHO_ROLES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            "inline-flex rounded px-2 py-0.5 text-[10px] font-semibold",
                            active
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-200 text-slate-700",
                          )}
                        >
                          {active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-[11px]"
                          disabled={busy || !onToggleAccess}
                          onClick={() => onToggleAccess?.(u, !active)}
                          data-testid={`equipo-permiso-toggle-${u.id}`}
                        >
                          {active ? "Desactivar permisos" : "Activar permisos"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ul
        className="m-0 list-none space-y-1.5 p-0 text-[11px] text-slate-600"
        data-testid="equipo-permisos-roles-summary"
      >
        {AUXILIARY_ROLES_GUIDE.map((row) => (
          <li key={row.key}>
            <span className="font-semibold text-[#0F2D5C]">{row.title}:</span> {row.body}
          </li>
        ))}
      </ul>
    </div>
  );
}
